import { AppBar, Grid, styled } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { NotificationsPopover } from 'src/features/Notifications';
import { useSocketEvent } from 'src/socket';
import { onUserStatusUpdate } from 'src/socket/listeners/user';
import { appLocationState, getCurrentSession } from 'src/state/restoreLocation';
import { getCurrentSection } from 'src/state/routing';

import { useUpdateUserStatusMutation } from '@modules/workspace/hooks/useUpdateUserStatusMutation';
import { useWorkspaceInfoActiveUsers } from '@modules/workspace/providers/WorkspaceInfo/hooks/useWorkspaceInfoActiveUsers';
import { ScreenRecord } from 'src/components/ScreenRecorder/ScreenRecorder';
import { VerticalDivider } from 'src/components/VerticalDivider/VerticalDivider';
import { useThemeSettingsContext } from 'src/components/settings/ThemeSettingsContext';
import { SMALL_MOBILE_BREAKPOINT } from 'src/config-global';
import { Support } from 'src/features/ActionsBar/Support';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { HideSideBarIconButton } from 'src/layouts/DashboardLayout/shared/HideSideBarIconButton';
import { ToggleTheme } from 'src/layouts/shared/Header/ToggleTheme';
import { NavButtons } from 'src/layouts/shared/NavButtons/NavButtons';
import { StyledToolbar } from 'src/layouts/shared/StyledToolbar/StyledToolbar';
import { onWorkspaceLeft } from 'src/socket/listeners/workspace.js';
import { AccountPopover } from './AccountPopover';
import { GlobalAlert } from './GlobalAlert/GlobalAlert';
import { OnlineUsersPopover } from './OnlineUsersPopover';
import Searchbar from './Searchbar';
import { UserMentions } from './UserMentions/UserMentions';
import { WorkspaceDropdown } from './WorkspaceDropdown';

export default function Header() {
  const theme = useTheme();
  const location = useLocation();

  const { headerColor } = useThemeSettingsContext();
  const { upMobile, isMobile } = useBreakpoints();

  const { updateUserStatus } = useUpdateUserStatusMutation();
  const { activeUsers } = useWorkspaceInfoActiveUsers();

  const addToHistory = appLocationState((s) => s.addToHistory);

  useSocketEvent(
    onUserStatusUpdate(({ userId, status }) => {
      updateUserStatus({ userId, status });
    }),
  );

  useSocketEvent(
    onWorkspaceLeft(({ user: { _id } }) => {
      const { removeSession } = appLocationState.getState();
      removeSession(_id);
      const session = getCurrentSession();
      window.location.pathname = session.lastPath;
    }),
  );

  useEffect(() => {
    const currentSection = getCurrentSection(location.pathname);

    if (currentSection) {
      addToHistory(currentSection, location.pathname);
    }
  }, [location.pathname, addToHistory]);

  const renderContent = (
    <StyledGrid container>
      <StyledGridItemLeft container item xs={2} md={6}>
        <HideSideBarIconButton />
        <WorkspaceDropdown />

        {upMobile && (
          <>
            <VerticalDivider
              height={20}
              color={alpha(theme.palette.common.black, 0.12)}
            />
            <NavButtons />
          </>
        )}
      </StyledGridItemLeft>

      <StyledGridItemRight container item xs={10} md={6}>
        {isMobile && (
          <Support
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              color: 'white',
            }}
          />
        )}
        {upMobile && <Searchbar />}

        <ToggleTheme />

        <ScreenRecord />

        <OnlineUsersPopover users={activeUsers} />

        <NotificationsPopover />

        <UserMentions />

        <AccountPopover />
      </StyledGridItemRight>
    </StyledGrid>
  );

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        zIndex: theme.zIndex.appBar + 1,
        width: 1,
        background: theme.palette[headerColor.color][headerColor.level],
        color: theme.palette.primary.contrastText,
        justifyContent: 'center',
        position: 'relative',
        padding: '0 !important',
      }}
    >
      <GlobalAlert />
      <StyledToolbar>{renderContent}</StyledToolbar>
    </AppBar>
  );
}

const StyledGrid = styled(Grid)(({ theme }) => ({
  '& .desktop': {
    display: 'none',
  },
  [theme.breakpoints.up(SMALL_MOBILE_BREAKPOINT)]: {
    '& .mobile': {
      display: 'none',
    },
    '& .desktop': {
      display: 'flex',
    },
  },
}));

const StyledGridItem = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexGrow: 1,
  gap: theme.spacing(0.5),
}));

const StyledGridItemLeft = styled(StyledGridItem)(({ theme }) => ({
  flexWrap: 'nowrap',
  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(1),
  },
}));

const StyledGridItemRight = styled(StyledGridItem)(({ theme }) => ({
  justifyContent: 'flex-end',
}));
